import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
} from "reactstrap";

import {
  getCommunicationList,
  getCommunicationChannelList,
  getCommunicationPlaceholderList,
  getNotificationTemplateList,
} from "../../../../store/communication/action";
import { getSmsTemplateList } from "../../../../store/sms/action";
import { emailTemplateList } from "../../../../store/CRMitEmailInbox/action";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import TableContainer from "../../../../Components/Common/TableContainer";
import NoDataContent from "../../../../Components/Common/NoDataContent";
import LocalDataSearch from "../../../../Components/SearchAndFilter";
import CommunicationChannelModal from "./CommunicationChannelModal";
import { usesSubscribers } from "./CommunicationSubscribers";
import { listCommunicationSubscriberApi } from "../../../../helpers/fakebackend_helper";
import SettingsPanel from "../../components/SettingsPanel";

const CHANNELS = ["EMAIL", "SMS", "NOTIFICATION"];

export default function CommunicationsSettings() {
  const dispatch = useDispatch();

  const [searchResultList, setSearchResultList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchTab] = useState("all");
  const [showFilter, setShowFilter] = useState(false);
  const [searchInActionShowLoading, setSearchInActionShowLoading] =
    useState(false);

  const [configModal, setConfigModal] = useState(false);
  const [selectedComm, setSelectedComm] = useState(null);

  const allowedSearchFilters = ["Id", "Code", "Title", "Category"];
  const allowedFiltersInit = [
    {
      useThisFilter: true,
      filterTitle: "Created Date Range",
      filterType: "DateRange",
      filterProperty: "CreatedDateTime",
      filterValue: { startDate: "", endDate: "" },
    },
  ];
  const [allowedFilters, setAllowedFilters] = useState(allowedFiltersInit);

  const { communicationListAPI, communicationListLoading, channelListAPI } =
    useSelector((state) => ({
      communicationListAPI:
        state.CommunicationReducer?.communicationList?.communication_list || [],
      communicationListLoading:
        state.CommunicationReducer?.communicationListLoading,
      channelListAPI:
        state.CommunicationReducer?.communicationChannelList
          ?.communicationchannel_list || [],
    }));

  const toggleShowFilter = () => setShowFilter((prev) => !prev);

  // Load the communications + every list the channel-config modal needs.
  useEffect(() => {
    dispatch(getCommunicationList());
    dispatch(getCommunicationChannelList());
    dispatch(getCommunicationPlaceholderList());
    dispatch(getNotificationTemplateList());
    dispatch(getSmsTemplateList());
    dispatch(emailTemplateList());
  }, [dispatch]);

  // Change #100528 - active staff subscribers, for the Subscribers column.
  // Refreshed whenever the configure modal closes, since that is where they change.
  const [subscriberList, setSubscriberList] = useState([]);
  useEffect(() => {
    if (configModal) return;
    let cancelled = false;
    listCommunicationSubscriberApi().then((res) => {
      if (!cancelled) setSubscriberList(res?.communicationsubscriber_list || []);
    });
    return () => {
      cancelled = true;
    };
  }, [configModal]);

  const subscriberCount = (communicationId) =>
    new Set(
      subscriberList
        .filter(
          (s) =>
            String(s.Communication_Id) === String(communicationId) &&
            (s.FlagActive === "1" || s.FlagActive === 1) &&
            s.Staff_Id
        )
        .map((s) => String(s.Staff_Id))
    ).size;

  const handleConfigure = (communication) => {
    setSelectedComm(communication);
    setConfigModal(true);
  };

  const toggleConfig = () => setConfigModal((prev) => !prev);

  // How many channels are enabled for a given communication (table badge).
  const enabledChannelCount = (communicationId) =>
    channelListAPI.filter(
      (ch) =>
        String(ch.Communication_Id) === String(communicationId) &&
        (ch.FlagCompanyEnabled === "1" || ch.FlagCompanyEnabled === 1)
    ).length;

  const columns = useMemo(
    () => [
      {
        Header: "Code",
        accessor: "Code",
        filterable: true,
        Cell: (cellProps) => (
          <span className="fw-semibold">{cellProps.row.original?.Code}</span>
        ),
      },
      {
        Header: "Title",
        accessor: "Title",
        filterable: true,
      },
      {
        Header: "Category",
        accessor: "Category",
        filterable: true,
        Cell: (cellProps) =>
          cellProps.row.original?.Category ? (
            <span className="badge bg-light text-muted">
              {cellProps.row.original.Category}
            </span>
          ) : null,
      },
      {
        Header: "Channels On",
        filterable: false,
        Cell: (cellProps) => {
          const count = enabledChannelCount(cellProps.row.original?.Id);
          return (
            <span
              className={`badge ${
                count > 0
                  ? "bg-success-subtle text-success"
                  : "bg-light text-muted"
              }`}
            >
              {count} / {CHANNELS.length}
            </span>
          );
        },
      },
      {
        Header: "Staff Subscribers",
        filterable: false,
        Cell: (cellProps) => {
          const row = cellProps.row.original;
          if (!usesSubscribers(row)) {
            return <span className="text-muted">-</span>;
          }
          const count = subscriberCount(row?.Id);
          return (
            <span
              className={`badge ${
                count > 0
                  ? "bg-primary-subtle text-primary"
                  : "bg-light text-muted"
              }`}
            >
              {count}
            </span>
          );
        },
      },
      {
        Header: "System",
        accessor: "FlagSystem",
        filterable: false,
        Cell: (cellProps) => {
          const value = cellProps.row.original?.FlagSystem;
          return value === "1" || value === 1 ? (
            <span className="badge bg-info-subtle text-info">System</span>
          ) : (
            <span className="badge bg-light text-muted">Custom</span>
          );
        },
      },
      {
        Header: "Active",
        accessor: "FlagActive",
        filterable: false,
        Cell: (cellProps) => {
          const value = cellProps.row.original?.FlagActive;
          return value === "1" || value === 1 ? (
            <span className="badge bg-success-subtle text-success">Active</span>
          ) : (
            <span className="badge bg-danger-subtle text-danger">Inactive</span>
          );
        },
      },
      {
        Header: "Action",
        Cell: (cellProps) => (
          <Button
            color="soft-primary"
            size="sm"
            onClick={() => handleConfigure(cellProps.row.original)}
          >
            <i className="ri-settings-3-line align-bottom me-1"></i>
            Configure
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelListAPI, subscriberList]
  );

  const showTable =
    !communicationListLoading &&
    !searchInActionShowLoading &&
    searchResultList?.length > 0;

  const showNoData =
    !communicationListLoading &&
    !searchInActionShowLoading &&
    communicationListAPI?.length === 0;

  const showNoResults =
    !communicationListLoading &&
    !searchInActionShowLoading &&
    communicationListAPI?.length > 0 &&
    searchResultList?.length === 0;

  return (
    <React.Fragment>
      {configModal && (
        <CommunicationChannelModal
          modal={configModal}
          toggle={toggleConfig}
          communication={selectedComm}
        />
      )}

      <LocalDataSearch
        Page="CommunicationSettings"
        ReceivedDataAPI={communicationListAPI}
        setSearchInActionShowLoading={setSearchInActionShowLoading}
        activeSearchTab={activeSearchTab}
        allowedSearchFilters={allowedSearchFilters}
        searchQuery={searchQuery}
        setSearchFilteredResults={setSearchResultList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        toggleShowFilter={toggleShowFilter}
        allowedFiltersInit={allowedFiltersInit}
        allowedFilters={allowedFilters}
        setAllowedFilters={setAllowedFilters}
      />

      <SettingsPanel
        title="Communications"
        description="Channels used to reach customers"
      >
        <Row>
          <Col lg={12}>
            <Card id="communicationList">
              <CardHeader className="border-0">
                <Row className="g-4 align-items-center">
                  <Col sm={4}>
                    <div className="search-box">
                      <Input
                        type="search"
                        className="form-control search"
                        placeholder="Search communications..."
                        onChange={(e) => setSearchQuery(e.target.value)}
                        value={searchQuery}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                {communicationListLoading || searchInActionShowLoading ? (
                  <LoadingSpinner />
                ) : (
                  <>
                    {showTable && (
                      <TableContainer
                        columns={columns}
                        data={searchResultList}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        className="custom-header-css"
                        divClass="table-responsive table-card mb-0"
                        tableClass="align-middle table-wrap"
                        theadClass="text-muted"
                        isLeadsFilter={false}
                      />
                    )}

                    {showNoData && (
                      <NoDataContent
                        titleText="No Communications Found"
                        paragraphText="Communications are seeded by the system. If none appear, check that the backend has been provisioned."
                        useButton={false}
                      />
                    )}

                    {showNoResults && (
                      <NoDataContent
                        titleText={`No Results for: ${searchQuery}`}
                        paragraphText="Try different search terms or remove filters."
                        useButton={false}
                      />
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>
    </React.Fragment>
  );
}
